import { createSlice } from "@reduxjs/toolkit";
import { IPurchaseState } from "./purchase.interface";
import { addPurchase, getPurchases } from "./purchase.action";
import { searchDistributor } from "../distributors/distributors.actions";
import { searchProducts } from "../products/products.action";

const initialState: IPurchaseState = {
  filters: {
    search: "",
    sort: "inserted",
    order: "desc",
    page: 0,
    limit: 10,
  },
  selectedPurchase: null,
  selectedPurchases: [],
  addPurchaseModal: false,

  // Get Purchase
  isGetPurchaseLoading: false,
  getPurchaseError: "",
  purchases: [],
  pagination: {
    length: 0,
    size: 0,
    page: 0,
  },

  // Add Purchase
  isAddPurchaseLoading: false,
  addPurchaseError: "",
  addPurchaseSuccess: false,

  // Search Distributor
  isSearchingDistributor: false,
  searchingDistributorError: "",
  searchedDistributors: [],

  // Search Product
  isSearchingProduct: false,
  searchingProductError: "",
  searchedProducts: [],

  // Payload
  products: [],
  distributor: null,
  invoiceID: "",
  invoiceDate: "",
  totalAmount: 0,
};

const purchaseSlice = createSlice({
  name: "purchase",
  initialState,
  reducers: {
    resetAddPurchase: (state) => {
      state.isAddPurchaseLoading = false;
      state.addPurchaseSuccess = false;
      state.addPurchaseError = "";
    },
    setAddPurchaseModal: (state, action) => {
      state.addPurchaseModal = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setSelectedPurchases: (state, action) => {
      state.selectedPurchases = action.payload;
    },
    setSelectedPurchase: (state, action) => {
      state.selectedPurchase = action.payload;
    },
    resetGetPurchase: (state) => {
      state.isGetPurchaseLoading = false;
      state.getPurchaseError = "";
      state.purchases = [];
      state.pagination = {
        length: 0,
        size: 0,
        page: 0,
      };
    },

    // Search Distributor
    resetSearchDistributor: (state) => {
      state.isSearchingDistributor = false;
      state.searchingDistributorError = "";
      state.searchedDistributors = [];
    },

    // Search Product
    resetSearchProduct: (state) => {
      state.isSearchingProduct = false;
      state.searchingProductError = "";
      state.searchedProducts = [];
    },

    // Set Payload
    setProducts: (state, action) => {
      state.products = action.payload;
    },
    setDistributor: (state, action) => {
      state.distributor = action.payload;
    },
    setInvoiceID: (state, action) => {
      state.invoiceID = action.payload;
    },
    setInvoiceDate: (state, action) => {
      state.invoiceDate = action.payload;
    },
    setTotalAmount: (state, action) => {
      state.totalAmount = action.payload;
    },

    // Reset Payload
    resetPayload: (state) => {
      state.products = [];
      state.distributor = null;
      state.invoiceID = "";
      state.invoiceDate = "";
      state.totalAmount = 0;
    },
  },
  extraReducers: (builder) => {
    // Get Purchases
    builder.addCase(getPurchases.pending, (state) => {
      state.isGetPurchaseLoading = true;
    });

    builder.addCase(getPurchases.fulfilled, (state, action) => {
      state.isGetPurchaseLoading = false;
      state.purchases = action.payload.purchaseOrders || [];
      state.pagination = action.payload.pagination;
    });

    builder.addCase(getPurchases.rejected, (state, action) => {
      state.isGetPurchaseLoading = false;
      state.getPurchaseError = action.payload || "Failed to get purchases";
    });

    // Search Distributor
    builder.addCase(searchDistributor.pending, (state) => {
      state.isSearchingDistributor = true;
    });

    builder.addCase(searchDistributor.fulfilled, (state, action) => {
      state.isSearchingDistributor = false;
      state.searchedDistributors = action.payload;
    });

    builder.addCase(searchDistributor.rejected, (state, action) => {
      state.isSearchingDistributor = false;
      state.searchingDistributorError =
        action.payload || "Failed to search distributor";
    });

    // Search Product
    builder.addCase(searchProducts.pending, (state) => {
      state.isSearchingProduct = true;
    });

    builder.addCase(searchProducts.fulfilled, (state, action) => {
      state.isSearchingProduct = false;
      state.searchedProducts = action.payload;
    });

    builder.addCase(searchProducts.rejected, (state, action) => {
      state.isSearchingProduct = false;
      state.searchingProductError =
        action.payload || "Failed to search product";
    });

    // Add Purchase
    builder.addCase(addPurchase.pending, (state) => {
      state.isAddPurchaseLoading = true;
    });

    builder.addCase(addPurchase.fulfilled, (state) => {
      state.isAddPurchaseLoading = false;
      state.addPurchaseSuccess = true;
    });

    builder.addCase(addPurchase.rejected, (state, action) => {
      state.isAddPurchaseLoading = false;
      state.addPurchaseError = action.payload || "Failed to add purchase";
    });
  },
});

export default purchaseSlice.reducer;

export const {
  resetAddPurchase,
  setAddPurchaseModal,
  setFilters,
  setSelectedPurchases,
  setSelectedPurchase,
  resetGetPurchase,
  resetSearchDistributor,
  resetSearchProduct,
  setProducts,
  setDistributor,
  setInvoiceID,
  setInvoiceDate,
  setTotalAmount,
  resetPayload,
} = purchaseSlice.actions;
